import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import SectionHeading from "@/components/base/SectionHeading";
import NewsCard from "@/components/base/NewsCard";
import WorkCard from "@/components/base/WorkCard";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import TopicGrid from "@/pages/home/components/TopicGrid";
import { newsList, sortNews, workList } from "@/utils/anime";
import { animeTopics } from "@/mocks/topics";
import { heroSlides } from "@/mocks/hero";
import type { AnimeTopic, HeroSlide } from "@/types/anime";

const topics = animeTopics as unknown as AnimeTopic[];
const slides = heroSlides as unknown as HeroSlide[];

export default function Home() {
  const latestNews = sortNews(newsList, "latest").slice(0, 6);
  const hotWorks = [...workList].sort((a, b) => b.score - a.score).slice(0, 5);

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <HeroCarousel slides={slides} />

        <section className="w-full border-b border-background-200 bg-background-50">
          <div className="mx-auto max-w-7xl px-4 py-14 md:px-8 md:py-16">
            <SectionHeading
              icon="ri-newspaper-line"
              title="最新动漫资讯"
              subtitle="按发布时间倒序，追踪动画、剧场版与漫画的一手动态"
              moreLabel="查看全部资讯"
              moreTo="/news"
            />
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {latestNews.map((news) => (
                <NewsCard key={news.id} news={news} />
              ))}
            </div>
          </div>
        </section>

        <section className="w-full border-b border-background-200 bg-background-100">
          <div className="mx-auto max-w-7xl px-4 py-14 md:px-8 md:py-16">
            <SectionHeading
              icon="ri-fire-line"
              title="热门动画作品推荐"
              subtitle="精选高口碑与高人气作品，快速发现下一部想追的番"
              moreLabel="更多作品推荐"
              moreTo="/recommendations"
            />
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
              {hotWorks.map((work) => (
                <WorkCard key={work.id} work={work} />
              ))}
            </div>
          </div>
        </section>

        <section className="w-full border-b border-background-200 bg-background-50">
          <div className="mx-auto max-w-7xl px-4 py-14 md:px-8 md:py-16">
            <SectionHeading
              icon="ri-bookmark-3-line"
              title="专题合集入口"
              subtitle="围绕主题聚合内容，一次看个够"
            />
            <TopicGrid topics={topics} />
          </div>
        </section>

        <section className="w-full bg-secondary-950">
          <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-8 px-4 py-14 md:px-8 md:py-16 lg:grid-cols-2">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full bg-accent-500/15 px-3 py-1 text-xs font-medium text-accent-400">
                <i className="ri-information-line" />
                关于78动漫
              </span>
              <h2 className="mt-4 font-heading text-2xl font-bold leading-tight text-background-50 md:text-3xl">
                让每一位动漫爱好者，更快找到想看的作品
              </h2>
              <p className="mt-4 max-w-xl text-sm leading-relaxed text-background-300">
                78动漫汇聚最新动画资讯、作品推荐与内容解读，通过分类筛选、搜索与收藏等功能，帮助你构建属于自己的动漫内容浏览体验。站内所有内容均为虚构示范。
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/about"
                  className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
                >
                  了解平台
                  <i className="ri-arrow-right-line text-base" />
                </Link>
                <Link
                  to="/recommendations"
                  className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/30 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/10 cursor-pointer"
                >
                  浏览作品库
                </Link>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { value: "1,200+", label: "收录资讯", icon: "ri-file-list-3-line" },
                { value: "480+", label: "推荐作品", icon: "ri-movie-line" },
                { value: "60+", label: "专题合集", icon: "ri-bookmark-3-line" },
                { value: "每日更新", label: "内容频率", icon: "ri-refresh-line" },
              ].map((stat) => (
                <div
                  key={stat.label}
                  className="rounded-lg border border-background-50/10 bg-background-50/5 p-5"
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-500/20 text-primary-300">
                    <i className={`${stat.icon} text-xl`} />
                  </span>
                  <p className="mt-3 font-heading text-xl font-bold text-background-50">{stat.value}</p>
                  <p className="mt-1 text-xs text-background-400">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}